import { useCallback } from 'react';
import intl from 'react-intl-universal';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CENTRALIZED_TASKS_TABLE_TABS } from 'utils/constants/centralizedTasks';
import {
  extractFilenameFromContentDisposition,
  createDownloadLinkAndDownloadFile,
} from 'utils/csvExports';
import { exportTasksList } from 'api/tasksList';
import useSnackbarNotification from 'Hooks/useSnackbarNotification';
import { useTeamTasks } from '../hooks/useTeamTasks/useTeamTasks';
import TasksListTable from 'Pages/TasksListPage/components/v2/TasksListTable/TasksListTable';
import SkeletonLoader from 'Organisms/TableSkeletonLoader/PageTableLoader';

const TeamTasks = () => {
  const { normalizedTasksState = false } = useFlags();
  const {
    isLoading,
    hasError,
    teamTasks,
    teamTasksSorting,
    teamTasksTableFilters,
    canFetchMoreTasks,
    totalCount,
    refreshTasks,
    handleUpdateFilters,
    handleSearchFilterChange,
    handleTableSort,
    handleFetchMoreTasks,
  } = useTeamTasks({ useNormalizedState: normalizedTasksState });

  const {
    SnackbarNotification,
    showNotification,
    isNotificationVisible,
    snackbarProps,
  } = useSnackbarNotification();

  const handleExportTasks = useCallback(async () => {
    try {
      const response = await exportTasksList({ ...teamTasksTableFilters });
      const filename = extractFilenameFromContentDisposition(
        response.headers['content-disposition']
      );
      const blob = new Blob([response.data], { type: 'text/csv' });
      createDownloadLinkAndDownloadFile(blob, filename);
    } catch (error) {
      showNotification({
        notificationVariant: 'error',
        notificationTitle: intl.get('TASKS_LIST_PAGE.EXPORT_TASKS_ERROR.TITLE'),
        notificationMessage: intl.get(
          'TASKS_LIST_PAGE.EXPORT_TASKS_ERROR.MESSAGE'
        ),
      });
    }
  }, [teamTasksTableFilters, showNotification]);

  if (isLoading) {
    return (
      <SkeletonLoader
        data-testid={`${CENTRALIZED_TASKS_TABLE_TABS.TEAM_TASKS}__skeleton-loader`}
      />
    );
  }

  return (
    <>
      <TasksListTable
        tableName={CENTRALIZED_TASKS_TABLE_TABS.TEAM_TASKS}
        tasks={teamTasks}
        filters={teamTasksTableFilters}
        totalCount={totalCount}
        onUpdateFilters={handleUpdateFilters}
        sortBy={teamTasksSorting.sortBy}
        order={teamTasksSorting.order}
        handleSort={handleTableSort}
        canFetchMoreTasks={canFetchMoreTasks}
        fetchMoreTasks={handleFetchMoreTasks}
        onExport={handleExportTasks}
        onSearchFilterChange={handleSearchFilterChange}
        refreshTasks={refreshTasks}
        hasError={hasError}
      />
      {isNotificationVisible && snackbarProps && (
        <SnackbarNotification {...snackbarProps} />
      )}
    </>
  );
};

export default TeamTasks;
